import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount)
}

/**
 * Compact currency in the app's house style: "$820", "$4.1k", "$52k", "$1.2M",
 * "−$500". Lowercase k, uppercase M, U+2212 minus — the single shared shape
 * (several pages previously hand-rolled variants that disagreed on all three).
 * Numbers under $1,000 fall back to full integer form so we don't end up with
 * weird "$50.0" shapes.
 */
export function formatCurrencyCompact(amount: number): string {
  const sign = amount < 0 ? '−' : ''
  const abs = Math.abs(amount)
  if (abs < 1000) return `${sign}${formatCurrency(abs, 0)}`
  if (abs < 1_000_000) {
    const v = abs / 1000
    return `${sign}$${v >= 10 ? v.toFixed(0) : v.toFixed(1)}k`
  }
  const v = abs / 1_000_000
  return `${sign}$${v >= 10 ? v.toFixed(0) : v.toFixed(1)}M`
}

/**
 * Median of a list of numbers, or null when the list is empty. Even-length
 * lists take the mean of the two middle values. Preferred over a mean for the
 * "typical month" figures on the cashflow page: one annual insurance bill or a
 * moving month shouldn't move the reference line the other eleven are read
 * against.
 */
export function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length / 2
  return sorted.length % 2 === 1
    ? sorted[Math.floor(mid)]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Format a stored date as the calendar day the user picked, ignoring timezone.
 *
 * Dates like entryDate / transaction.date are stored as UTC midnight
 * ("2026-05-15T00:00:00Z") to represent a calendar day. Passing that to
 * `new Date()` + date-fns `format` renders it in local time — anywhere west
 * of UTC, midnight UTC is the previous calendar day, so "May 15" silently
 * becomes "May 14". This helper slices the YYYY-MM-DD off the ISO string and
 * builds a Date at local midnight, so the day always matches what was stored.
 */
export function formatDateOnly(iso: string | Date, fmt = 'MMM d, yyyy'): string {
  const s = typeof iso === 'string' ? iso : iso.toISOString()
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  return format(new Date(y, m - 1, d), fmt)
}

/**
 * Today as a YYYY-MM-DD string in the user's local timezone. Used as the
 * default value for <input type="date">. Avoid `new Date().toISOString()` —
 * it returns UTC, which can return tomorrow's date for evening users west of
 * UTC.
 */
export function todayLocalISO(): string {
  return format(new Date(), 'yyyy-MM-dd')
}